import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";

/**
 * History of automatic page snapshots (taken before each approved inbound change
 * and before each restore). Lets the admin view a snapshot's tasks and restore
 * the page to it. Restore itself snapshots the current state first, so it's undoable.
 */
const REASON_LABEL = {
  before_inbound_apply: "Before approving sheet changes",
  before_restore: "Before a restore",
  manual: "Manual snapshot",
};

const fmtPrice = (v, symbol) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (n === 0) return "Free";
  return symbol + (Number.isInteger(n) ? n.toString() : n.toFixed(2));
};

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function SnapshotsModal({ open, sheetId, currencySymbol = "$", onClose, onRestore }) {
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // snapshot id -> payload
  const [confirmId, setConfirmId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setExpanded(null);
    setConfirmId(null);
    api
      .get(`/sheets/${sheetId}/snapshots`)
      .then((r) => setSnaps(r.data))
      .catch(() => setSnaps([]))
      .finally(() => setLoading(false));
  }, [open, sheetId]);

  const toggleView = async (id) => {
    if (expanded?.id === id) {
      setExpanded(null);
      return;
    }
    const { data } = await api.get(`/sheets/${sheetId}/snapshots/${id}`);
    setExpanded(data);
  };

  if (!open) return null;

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card snapshots-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 620 }}
      >
        <h3>Snapshots &amp; history</h3>
        <p className="muted" style={{ marginTop: 8 }}>
          A copy of this page is saved automatically before any incoming change is
          applied. Restore to roll the whole page back to that point.
        </p>

        <div className="snapshots-list">
          {loading && <div className="muted" style={{ padding: 16 }}>Loading…</div>}
          {!loading && snaps.length === 0 && (
            <div className="empty" style={{ padding: 24 }}>No snapshots yet.</div>
          )}
          {snaps.map((s) => (
            <div className="snapshot-item" key={s.id}>
              <div className="snapshot-row">
                <div className="snapshot-meta">
                  <span className="snapshot-reason">{REASON_LABEL[s.reason] || s.reason}</span>
                  <span className="snapshot-when">{fmtWhen(s.created_at)}</span>
                  <span className="snapshot-count">{s.task_count} task{s.task_count === 1 ? "" : "s"}</span>
                </div>
                <div className="row-h" style={{ gap: 8 }}>
                  <button className="ghost" onClick={() => toggleView(s.id)}>
                    {expanded?.id === s.id ? "Hide" : "View"}
                  </button>
                  {confirmId === s.id ? (
                    <>
                      <button className="danger-action" onClick={() => { setConfirmId(null); onRestore(s.id); }}>
                        Confirm restore
                      </button>
                      <button onClick={() => setConfirmId(null)}>Cancel</button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmId(s.id)}>Restore</button>
                  )}
                </div>
              </div>
              {expanded?.id === s.id && (
                <div className="snapshot-detail">
                  <table className="snapshot-table">
                    <thead>
                      <tr>
                        <th className="num">#</th>
                        <th>Task</th>
                        <th className="num">Price</th>
                        <th>Agreed</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(expanded.payload?.tasks || []).map((t) => (
                        <tr key={t.page_seq}>
                          <td className="num">{t.project_seq ?? "—"}</td>
                          <td>{t.name}</td>
                          <td className="num">{fmtPrice(t.client_estimate_price, currencySymbol)}</td>
                          <td>{t.agreed ? "Yes" : "No"}</td>
                          <td>{t.client_notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20 }}>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
